import VideoPlayer from "@dana/Components/VideoPlayer";
import PrevArrowIcon from "@dana/Icons/PrevArrowIcon";
import Search from "@dana/MobiusOverrides/Icons/Search";
import Youtube from "@dana/MobiusOverrides/Icons/Youtube";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { headings } from "@dana/MobiusOverrides/styles/typography";
import { EZIDAttributeFilter } from "@dana/Types/EZIDAttributeFilter";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Badge, { BadgeTypeValues } from "@insite/mobius/Badge";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface UJointStyleSelectorScreenProps {
    video?: LinkFieldValue;
    caption?: LinkFieldValue;
    backLabel?: string;
    slideTitle?: string;
    subText?: string;
    description?: string;
    image?: string;
    imageAltText?: string;
    facetLabel?: string;
    leftNote?: string;
    rightNote?: string;
    watchVideoLabel?: string;
    stepLabel?: string;
    nextLabel?: string;
    step?: number;
    readyshaftCategoryId?: string;
    currentAttributeFilters?: EZIDAttributeFilter[];
    onProceedToNextScreen: (newAttributeFilters?: EZIDAttributeFilter[]) => void;
    onProceedToPreviousScreen: (newAttributeFilters?: EZIDAttributeFilter[]) => void;
}

export interface UJointStyleSelectorScreenStyles {
    wrapper?: InjectableCss;
    backButton?: ButtonPresentationProps;
    heading?: TypographyPresentationProps;
    subText?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    contentWrapper?: InjectableCss;
    twoColWrapper?: InjectableCss;
    watchButtonWrapper?: InjectableCss;
    actionWrapper?: InjectableCss;
    image?: ImgProps;
    videoModal?: ModalPresentationProps;
    watchVideoButton?: ButtonPresentationProps;
}

export const styles: UJointStyleSelectorScreenStyles = {
    wrapper: {
        css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 90%;
                gap: 24px;
                margin: 18px auto 0 auto;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                             width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
            `,
    },
    backButton: {
        variant: "tertiary",
        css: css`
                align-self: flex-start;
                text-align: left;
            `,
        icon: {
            src: PrevArrowIcon,
            position: "left",
        },
    },
    heading: {
        variant: "h3",
        css: css`
                margin-bottom: 0;
                text-align: center;
            `,
    },
    subText: {
        variant: "h4",
        css: css`
                text-align: center;
                margin-bottom: 0;
            `,
    },
    description: {
        variant: "h5",
        transform: "none",
        color: themeColors.primary.richBlack,
        css: css`
                    letter-spacing: inherit;
                    text-align: center;
                    margin-bottom: 0;
                `,
    },
    contentWrapper: {
        css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;
                border: 1px solid ${themeBackgrounds.lightGray};
                border-radius: 4px;
                padding: 16px;
                box-sizing: border-box;
    
                & > *:not(:last-child) {
                    border-bottom: 1px solid ${themeBackgrounds.lightGray};
                    padding-bottom: 16px;
                    margin-bottom: 16px;
                }
            `,
    },
    image: {
        css: css`
            width: auto;
            max-width: 100%;
            height: auto;
            display: block;
            object-fit: contain;
            `,
    },
    twoColWrapper: {
        css: css`
                display: flex;
                flex-direction: row;
                gap: 16px;
                width: 100%;
                & > * {
                    flex: 1 1 0;
                    min-width: 0;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 12px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        css`
                            flex-direction: column;
                            align-items: stretch;
                            gap: 14px;
                            & > * {
                                flex: unset;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            }
            `,
    },
    actionWrapper: {
        css: css`
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
                gap: 16px;
            `,
    },
    watchButtonWrapper: {
        css: css`
                border-bottom: none !important;
            `,
    },
    videoModal: {
        cssOverrides: {
            modalContent: css`
                video {
                    width: 100%;
                    height: auto;
                    max-height: 70vh;
                    display: block;
                }
            `,
        },
        sizeVariant: "large",
    },
    watchVideoButton: {
        variant: "primary",
        icon: {
            position: "left",
            src: () => <Youtube />
        },
    },
};

const UJointStyleSelectorScreen: React.FC<UJointStyleSelectorScreenProps> = ({
    video,
    caption,
    backLabel,
    slideTitle,
    subText,
    description,
    image,
    imageAltText,
    facetLabel,
    leftNote,
    rightNote,
    watchVideoLabel,
    stepLabel,
    nextLabel,
    step,
    readyshaftCategoryId,
    currentAttributeFilters,
    onProceedToNextScreen,
    onProceedToPreviousScreen,
}) => {
    const [showLoadingOverlay, setShowLoadingOverlay] = React.useState(true);
    const [uJointData, setUJointData] = React.useState<EZIDAttributeFilter[]>([]);
    const [selectedUJoint, setSelectedUJoint] = useState<string[]>([]);
    const [videoModalIsOpen, setVideoModalIsOpen] = useState(false);
    const videoLink = video ? useGetLink(video) : null;
    const captionLink = caption ? useGetLink(caption) : null;
    const [uJointSearchText, setUJointSearchText] = useState("");
    const searchedUJointData = uJointData.filter(uJoint =>
        uJoint.attributeValueName.toLowerCase().includes(uJointSearchText.toLowerCase())
    );

    useEffect(() => {
        const searchProducts = async () => {
            try {
                const result = await fetch("/api/v1/ezid/getProductFacets", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        categoryId: readyshaftCategoryId,
                        // Exclude U-Joint from the initial filters to get all U-Joint data
                        attributeValueIds: currentAttributeFilters?.filter(f => f.attributeTypeName?.toLowerCase() !== 'u-joint style')?.map(filter => filter.attributeValueId) || [],
                        groupName: "U-Joint Style",
                    }),
                });

                if (!result.ok) {
                    throw new Error(result.statusText);
                }

                const data = await result.json();
                populateCheckboxes(data);

            } catch (error) {
                console.error("Error fetching products:", error);
            } finally {
                setShowLoadingOverlay(false);
            }
        };

        const handleExistingFilters = () => {
            if (currentAttributeFilters) {
                const uJointFilters = currentAttributeFilters.filter(
                    filter => filter.attributeTypeName?.toLowerCase() === "u-joint style"
                );

                if (uJointFilters.length > 0) {
                    setSelectedUJoint(uJointFilters.map(filter => filter.attributeValueId));
                }
            }
        }

        handleExistingFilters();
        searchProducts();
    }, []);

    const populateCheckboxes = (apiData: any) => {
        if (apiData) {
            const facets = apiData.filter(
                (facet: { attributeTypeName: string }) => facet.attributeTypeName.toLowerCase() === "u-joint style");
            if (facets) {
                setUJointData(facets);
            }
        }
    };

    const onNextButtonClick = () => {
        // Remove all old items with attributeTypeName = "u-joint style"
        const noUJointAttributeFilters =
            currentAttributeFilters?.filter(f => f.attributeTypeName?.toLowerCase() !== "u-joint style") || [];

        // Add selected u-joint style from searchedUJointData
        const selectedUJointFilters = searchedUJointData
            .filter(ujoint => selectedUJoint.includes(ujoint.attributeValueId))
            .map(ujoint => ({
                attributeTypeId: ujoint.attributeTypeId,
                attributeTypeName: ujoint.attributeTypeName,
                attributeValueId: ujoint.attributeValueId,
                attributeValueName: ujoint.attributeValueName,
                count: ujoint.count,
            }));

        // Call onProceedToNextScreen with the new filters
        const newAttributeFilters = [...noUJointAttributeFilters, ...selectedUJointFilters];
        onProceedToNextScreen(newAttributeFilters);
    }

    const onBackButtonClick = () => {
        // Remove all filters with attributeTypeName = "u-joint style"
        const noUJointAttributeFilters =
            currentAttributeFilters?.filter(f => f.attributeTypeName?.toLowerCase() !== "u-joint style") || [];
        onProceedToPreviousScreen(noUJointAttributeFilters);
    }

    const videoModalCloseHandler = () => {
        setVideoModalIsOpen(false);
    };

    return (
        <LoadingOverlay
            loading={showLoadingOverlay}
            css={css`
                width: 100%;
            `}
        >
            <StyledWrapper {...styles.wrapper} className="wrapper">
                <Button
                    {...styles.backButton}
                    onClick={onBackButtonClick}>
                    {backLabel}
                </Button>
                <Typography {...styles.heading} as="h3">
                    {slideTitle}
                </Typography>
                <Typography {...styles.subText} as="h4">
                    {subText}
                </Typography>
                <Typography {...styles.description} as="h5">
                    {description}
                </Typography>
                <StyledWrapper {...styles.contentWrapper}>
                    <StyledWrapper {...styles.twoColWrapper}>
                        {image && (
                            <Img src={image} altText={imageAltText || ''} {...styles.image} />
                        )}
                        <StyledWrapper>
                            <Typography as="b">{facetLabel} ({uJointData.length})</Typography>
                            <TextField
                                className="u-joint-text-field"
                                id="uJointTextField"
                                value={uJointSearchText}
                                onChange={e => {
                                    setUJointSearchText(e.target.value);
                                }}
                                iconProps={{ src: Search }}
                            />
                            <CheckboxGroup>
                                {searchedUJointData.map((uJoint) => (
                                    <Checkbox
                                        checked={selectedUJoint.includes(uJoint.attributeValueId)}
                                        key={uJoint.attributeValueId}
                                        onChange={() => {
                                            setSelectedUJoint((prev) => {
                                                if (prev.includes(uJoint.attributeValueId)) {
                                                    return prev.filter(id => id !== uJoint.attributeValueId);
                                                }
                                                return [...prev, uJoint.attributeValueId];

                                            });
                                        }}
                                    >
                                        {`${uJoint.attributeValueName} (${uJoint.count})`}
                                    </Checkbox>
                                ))}
                            </CheckboxGroup>
                        </StyledWrapper>
                    </StyledWrapper>

                    <StyledWrapper {...styles.twoColWrapper}>
                        {leftNote && (
                            <Typography className="text-body-lg">
                                {parse(leftNote, parserOptions)}
                            </Typography>
                        )}
                        {rightNote && (
                            <Typography className="text-body-lg">
                                {parse(rightNote, parserOptions)}
                            </Typography>
                        )}
                    </StyledWrapper>
                    {videoLink?.url && (
                        <StyledWrapper {...styles.watchButtonWrapper}>
                            <Button {...styles.watchVideoButton} onClick={() => setVideoModalIsOpen(true)}>
                                {watchVideoLabel}
                            </Button>
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.actionWrapper}>
                        <Typography as="b">{stepLabel} {step}</Typography>
                        <Button variant="primary" onClick={onNextButtonClick} disabled={selectedUJoint.length === 0}>
                            {nextLabel}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <Modal
                {...styles.videoModal}
                id="uJointSelectorVideoModal"
                isOpen={videoModalIsOpen}
                handleClose={videoModalCloseHandler}>
                <StyledWrapper>
                    <VideoPlayer
                        videoUrl={videoLink?.url || ''}
                        videoCaptionUrl={captionLink?.url || ''}
                    />
                </StyledWrapper>
            </Modal>
        </LoadingOverlay>
    )
};

export default UJointStyleSelectorScreen;